import React from 'react';
import { observer, inject } from 'mobx-react';
import qs from 'qs';
import * as DataList from 'components/DataList';
import { stripHtml } from 'helpers/strings';
import { currencyFormattedAmount } from 'helpers/currency';
import { shortDate, clockTime } from 'components/Tables/ScheduleList';
import { COLUMNS, CARDS } from 'components/Tables/WorkReports';

// The work orders an equipment was serviced on (its page), laid out like the
// Work orders list. The customer column stays: equipment can move between
// clients, so older work orders may be a previous owner's.
const MIN_TABLE_WIDTH = 1330;

const STATUS_TONES = {
  draft: 'grey',
  submitted: 'blue',
  reviewed: 'purple',
  invoiced: 'green',
};

// Open and closed the way the server counts them for equipment
// (GET /equipments/:id/analytics): closed is submitted, anything else is open.
export const openAndClosed = (workOrders) => {
  const closed = workOrders.filter((workOrder) => workOrder.attributes.status === 'submitted').length;
  return { open: workOrders.length - closed, closed };
};

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

@inject('viewStore', 'equipmentStore')
@observer
class EquipmentWorkOrders extends React.Component {
  componentDidMount() {
    this.load();
  }

  load = () => {
    this.props.equipmentStore.loadEquipmentWorkReports(this.props.equipment.id);
  };

  renderCounts(objects) {
    const { open, closed } = openAndClosed(objects);
    return (
      <span className="eqp-wo-counts" title="Closed work orders are submitted; open ones are everything else. Newest first.">
        <DataList.Pill tone="amber">{open} open</DataList.Pill>
        <DataList.Pill tone="blue">{closed} closed</DataList.Pill>
      </span>
    );
  }

  render() {
    const { equipment, viewStore, equipmentStore } = this.props;
    const { objects, isLoading, isError, equipmentId } = equipmentStore.equipmentWorkReports;
    const section = (viewStore.jobTab && viewStore.jobTab.section) || 'details';

    if (isLoading || equipmentId !== equipment.id) {
      return <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun="work orders" rows={4} />;
    }
    if (isError) {
      return <DataList.LoadError noun="work orders" noFilters onRetry={this.load} />;
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="work orders"
          title="No work orders yet"
          message="Work orders that service this equipment will show up here."
        />
      );
    }

    return (
      <DataList.List
        preferTable
        columns={COLUMNS}
        cards={CARDS}
        minTableWidth={MIN_TABLE_WIDTH}
        total={objects.length}
        noun="work orders"
        note={this.renderCounts(objects)}
      >
        {objects.map((workReport) => {
          const attributes = workReport.attributes;
          const job = attributes.job || {};
          const client = attributes.client || {};
          const createdBy = attributes.created_by || {};
          const deleted = Boolean(attributes.deleted_at);
          const description = attributes.description ? stripHtml(attributes.description).trim() : '';
          const address = attributes.location && attributes.location.full_address;
          const showLink = `/dashboard/work-reports/${workReport.id}/show`;
          const jobLink = job.id ? `/dashboard/jobs/${job.id}/${section}` : null;
          const clientLink = client.id
            ? `/dashboard/clients/${client.id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`
            : undefined;
          return (
            <DataList.Row key={workReport.id}>
              <DataList.Cell area="number">
                <DataList.RowLink href={showLink} label={`Work order ${attributes.number}`}>
                  #{attributes.number}
                </DataList.RowLink>
              </DataList.Cell>
              <DataList.Cell area="job">
                {attributes.event_number ? (
                  <DataList.Lines primary={`Job #${attributes.event_number}`} href={jobLink || undefined} secondary={job.service_type_name} />
                ) : (
                  <DataList.Muted>No job</DataList.Muted>
                )}
              </DataList.Cell>
              <DataList.Cell area="date">
                <DataList.Lines
                  primary={<React.Fragment><DataList.CardOnly>Created </DataList.CardOnly>{shortDate(attributes.created_at)}</React.Fragment>}
                  secondary={clockTime(attributes.created_at)}
                  strong={false}
                />
              </DataList.Cell>
              <DataList.Cell area="client">
                {client.name ? (
                  <DataList.Lines primary={client.name} href={clientLink} secondary={address} />
                ) : (
                  <DataList.Muted>No customer</DataList.Muted>
                )}
              </DataList.Cell>
              <DataList.Cell area="author">
                {createdBy.full_name ? (
                  <DataList.Lines
                    primary={createdBy.full_name}
                    href={createdBy.id
                      ? `/dashboard/users/${createdBy.id}/show?${qs.stringify(viewStore.userShowSearch, { encode: false })}`
                      : undefined}
                    secondary={createdBy.email}
                  />
                ) : (
                  <DataList.Muted>Unknown</DataList.Muted>
                )}
              </DataList.Cell>
              <DataList.Cell area="details">
                <DataList.Lines
                  primary={description || <DataList.Muted>No description</DataList.Muted>}
                  title={description}
                  strong={false}
                  secondary={attributes.po_number ? `PO ${attributes.po_number}` : <DataList.Muted>No PO number</DataList.Muted>}
                />
              </DataList.Cell>
              <DataList.Cell area="total">
                <div className="dl-lines__primary">
                  <DataList.CardOnly>Total </DataList.CardOnly>
                  {currencyFormattedAmount(attributes.grand_total)}
                </div>
              </DataList.Cell>
              <DataList.Cell area="status">
                {deleted ? (
                  <DataList.Pill tone="red">Deleted</DataList.Pill>
                ) : (
                  <DataList.Pill tone={STATUS_TONES[attributes.status] || 'grey'}>{capitalise(attributes.status)}</DataList.Pill>
                )}
              </DataList.Cell>
              <DataList.Cell area="actions">
                <DataList.Actions
                  items={[
                    jobLink && { icon: 'eye-open', label: 'View job', href: jobLink },
                    { icon: 'edit', label: 'View or edit work order', href: showLink },
                  ]}
                />
              </DataList.Cell>
            </DataList.Row>
          );
        })}
      </DataList.List>
    );
  }
}

export default EquipmentWorkOrders;
